import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { ApiService } from "app/core/api/api.service";
import { Observable } from "rxjs";

function hasFiles(arr: any[] | undefined): boolean {
  return (
    Array.isArray(arr) &&
    arr.some((x) => x instanceof File || x instanceof Blob)
  );
}

function buildCourtFormData(courtData: any): FormData {
  const form = new FormData();

  // append scalars
  const appendScalar = (k: string, v: any) => {
    if (v === undefined || v === null) return;
    form.append(k, String(v));
  };

  // copy all known fields except pictures
  // (add/remove keys here as needed for your DTO)
  appendScalar("name", courtData.name);
  appendScalar("sports", courtData.sports);
  appendScalar("status", courtData.status);
  appendScalar("courtType", courtData.courtType);
  appendScalar("surfaceType", courtData.surfaceType);
  appendScalar("airConditioned", courtData.airConditioned);
  appendScalar("minCapacity", courtData.minCapacity);
  appendScalar("maxCapacity", courtData.maxCapacity);
  appendScalar("pricePerHour", courtData.pricePerHour);
  if (courtData.pricingRules !== undefined) {
    appendScalar("pricingRules", JSON.stringify(courtData.pricingRules));
  }

  // pictures: mix of strings (existing URLs) and File/Blob (new uploads)
  const pics = Array.isArray(courtData.pictures) ? courtData.pictures : [];
  for (const p of pics) {
    if (p instanceof File || p instanceof Blob) {
      const filename = (p as File).name ?? "upload";
      form.append("pictures", p, filename);
    } else if (typeof p === "string" && p.trim()) {
      // backend merges req.body.pictures (URLs) with req.files
      form.append("pictures", p.trim());
    }
  }

  // optional: support removePictures for updates
  if (Array.isArray(courtData.removePictures)) {
    for (const rp of courtData.removePictures) {
      if (typeof rp === "string" && rp.trim())
        form.append("removePictures", rp.trim());
    }
  }

  return form;
}

@Injectable({ providedIn: "root" })
export class CourtsService {
  private _apiService = inject(ApiService);
  constructor(private _httpClient: HttpClient) {}

  getAllCourts(
    venueId: string,
    page = 1,
    limit = 10,
    sortBy?: string,
  ): Observable<any> {
    const query = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (sortBy) query.set("sortBy", sortBy);
    return this._apiService.get(`/venue/${venueId}/courts?${query.toString()}`);
  }

  delete(venueId: string, courtId: any): Observable<any> {
    return this._apiService.delete(`venue/${venueId}/courts/${courtId}`);
  }

  /**
   * Sends JSON normally; if pictures contain File/Blob, switches to FormData automatically.
   * Keeps the same URLs you already use.
   */
  createOrUpdateCourt(venueId: string, courtData: any): Observable<any> {
    const isUpdate =
      courtData instanceof FormData
        ? courtData.has("id")
        : courtData.id !== undefined;

    const courtId =
      courtData instanceof FormData ? courtData.get("id") : courtData.id;

    if (courtData instanceof FormData) {
      // Handle FormData from component
      const url = isUpdate
        ? `venue/${venueId}/courts/${courtId}`
        : `venue/${venueId}/courts`;

      return isUpdate
        ? this._apiService.put(url, courtData)
        : this._apiService.post(url, courtData);
    } else {
      // Handle plain object (existing logic)
      const urlBase = `venues/${venueId}/courts`;
      const url = isUpdate ? `${urlBase}/${courtData.id}` : urlBase;

      if (hasFiles(courtData?.pictures)) {
        const form = buildCourtFormData(courtData);
        return isUpdate
          ? this._apiService.put(
              `venue/${venueId}/courts/${courtData.id}`,
              form,
            )
          : this._apiService.post(`venue/${venueId}/courts`, form);
      }

      return isUpdate
        ? this._apiService.put(
            `venue/${venueId}/courts/${courtData.id}`,
            courtData,
          )
        : this._apiService.post(`venue/${venueId}/courts`, courtData);
    }
  }
  /**
   * Get all sports
   * @returns Observable with all sports data
   */
  getAllSports(): Observable<any> {
    return this._apiService.get(`sports`);
  }

  /**
   * Get specific sport by name
   * @param sportName - The name of the sport (e.g., 'padel', 'tennis', 'basketball')
   * @returns Observable with the specific sport data
   */
  getSportByName(sportName: string): Observable<any> {
    return this._apiService.get(`sports/${sportName}`);
  }
}
